/**
 * Deterministic topological ordering for workflow snapshot nodes.
 *
 * Execution and rendering order in a workflow is defined by its edges, not by
 * the per-node `orderIndex` (which is creation/insertion order — see the
 * `@deprecated` note on `workflowNodes.orderIndex`). This helper derives the
 * edge order so callers don't have to trust `orderIndex`.
 *
 * Pure TS — no React / DOM / Effect deps. Shared by apps/web (proposal diff
 * view) and apps/api (snapshot composition) so both order nodes identically.
 */

/** Minimal node shape: an id plus an optional legacy `orderIndex` field. */
export interface TopoNode {
  id: string;
  orderIndex?: number;
}

/** Minimal edge shape using the canonical snapshot field names. */
export interface TopoEdge {
  sourceNodeId: string;
  targetNodeId: string;
}

/**
 * Compare two nodes for a stable, deterministic tiebreak when topology leaves
 * the order ambiguous (multiple roots, parallel branch siblings, disconnected nodes).
 * Intentionally ignores legacy `orderIndex` so execution, previews, and diffs share
 * one edge-derived order.
 */
function compareNodes<N extends TopoNode>(a: N, b: N): number {
  return a.id.localeCompare(b.id);
}

/**
 * Return `nodes` ordered topologically (roots first) according to `edges`,
 * using Kahn's algorithm. When several nodes are simultaneously ready, the
 * id tiebreak keeps the output deterministic. Nodes left over by a cycle,
 * plus any disconnected nodes, are appended in tiebreak order.
 *
 * When there are no edges at all, falls back to the tiebreak order.
 */
export function topoOrderNodes<N extends TopoNode, E extends TopoEdge>(
  nodes: N[],
  edges: E[]
): N[] {
  if (edges.length === 0) {
    return [...nodes].sort(compareNodes);
  }

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();
  for (const n of nodes) {
    inDegree.set(n.id, 0);
    adjacency.set(n.id, []);
  }
  for (const e of edges) {
    // Ignore edges that point outside this node set (e.g. dangling refs).
    if (!byId.has(e.sourceNodeId) || !byId.has(e.targetNodeId)) continue;
    adjacency.get(e.sourceNodeId)!.push(e.targetNodeId);
    inDegree.set(e.targetNodeId, (inDegree.get(e.targetNodeId) ?? 0) + 1);
  }

  const cmpId = (a: string, b: string) => compareNodes(byId.get(a)!, byId.get(b)!);

  // `ready` holds every zero-in-degree node, kept sorted so we always emit the
  // smallest by tiebreak. Re-sorting a small array each step is cheaper than a
  // heap for the node counts a workflow ever reaches.
  const ready: string[] = [];
  for (const [id, deg] of inDegree) if (deg === 0) ready.push(id);
  ready.sort(cmpId);

  const orderedIds: string[] = [];
  const visited = new Set<string>();
  while (ready.length > 0) {
    const id = ready.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    orderedIds.push(id);
    for (const child of adjacency.get(id) ?? []) {
      const newDeg = (inDegree.get(child) ?? 1) - 1;
      inDegree.set(child, newDeg);
      if (newDeg === 0) ready.push(child);
    }
    ready.sort(cmpId);
  }

  const ordered = orderedIds.map((id) => byId.get(id)!);
  // Cycle / disconnected remnants Kahn's couldn't drain — append deterministically.
  const remaining = nodes.filter((n) => !visited.has(n.id)).sort(compareNodes);
  return [...ordered, ...remaining];
}

/**
 * Return IDs for nodes that cannot be reached from any connected root.
 *
 * A connected root is a node with in-degree 0 and at least one outgoing edge.
 * Fully disconnected nodes are unreachable when another connected subgraph exists.
 * If every node participates only in a rootless cycle, all nodes are unreachable;
 * callers that already emit a "no entry node" issue can gate that case out.
 */
export function findUnreachableIds<N extends TopoNode, E extends TopoEdge>(
  nodes: N[],
  edges: E[]
): string[] {
  if (nodes.length <= 1 || edges.length === 0) return [];

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();
  for (const node of nodes) {
    inDegree.set(node.id, 0);
    adjacency.set(node.id, []);
  }

  for (const edge of edges) {
    if (!byId.has(edge.sourceNodeId) || !byId.has(edge.targetNodeId)) continue;
    adjacency.get(edge.sourceNodeId)!.push(edge.targetNodeId);
    inDegree.set(edge.targetNodeId, (inDegree.get(edge.targetNodeId) ?? 0) + 1);
  }

  const roots = nodes
    .filter(
      (node) => (inDegree.get(node.id) ?? 0) === 0 && (adjacency.get(node.id) ?? []).length > 0
    )
    .map((node) => node.id);

  const visited = new Set<string>();
  const queue = [...roots].sort((a, b) => a.localeCompare(b));
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    for (const target of adjacency.get(current) ?? []) {
      if (!visited.has(target)) queue.push(target);
    }
  }

  return nodes.filter((node) => !visited.has(node.id)).map((node) => node.id);
}
